import type { ISignalDataResponse } from "../../scheme/components/analytics/interfaces";
import { createPromiseCache } from "./promise-cache";
import { normalizeResponseArray } from "./response-array";

export interface IMultiAnalysisSignalMeasureRef {
  idMeasure: number;
  date: string;
}

interface IMultiAnalysisSignalApi {
  fetchJson: (args: {
    url: string;
    method: "POST";
    data: {
      measures: readonly IMultiAnalysisSignalMeasureRef[];
      maxPoints?: number;
    };
  }) => Promise<unknown>;
}

const SIGNAL_DATA_CACHE_LIMIT = 8;
export const SIGNAL_DATA_BATCH_SIZE = 100;
const signalDataCache = createPromiseCache<ISignalDataResponse[]>({ maxEntries: SIGNAL_DATA_CACHE_LIMIT });

export const buildSignalDataCacheKey = (measureRefs: readonly IMultiAnalysisSignalMeasureRef[]): string =>
  JSON.stringify(measureRefs.map(({ idMeasure, date }) => [idMeasure, date]));

export const fetchMultiAnalysisSignalData = async (
  api: IMultiAnalysisSignalApi,
  measureRefs: readonly IMultiAnalysisSignalMeasureRef[],
): Promise<ISignalDataResponse[]> => {
  if (!measureRefs.length) {
    return [];
  }

  const cacheKey = `${buildSignalDataCacheKey(measureRefs)}:"full"`;
  return signalDataCache.getOrCreate(cacheKey, () =>
    api
      .fetchJson({
        url: "/point-v2/measure/signals/data/batch",
        method: "POST",
        data: { measures: measureRefs },
      })
      .then(response => normalizeResponseArray(response as ISignalDataResponse[] | null | undefined)),
  );
};

export const fetchMultiAnalysisSignalDataInChunks = async (
  api: IMultiAnalysisSignalApi,
  measureRefs: readonly IMultiAnalysisSignalMeasureRef[],
  batchSize = SIGNAL_DATA_BATCH_SIZE,
): Promise<ISignalDataResponse[]> => {
  if (!measureRefs.length) {
    return [];
  }

  const size = Math.max(1, batchSize);
  const result: ISignalDataResponse[] = [];

  for (let index = 0; index < measureRefs.length; index += size) {
    const chunk = measureRefs.slice(index, index + size);
    result.push(...(await fetchMultiAnalysisSignalData(api, chunk)));
  }

  return result;
};
